import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import type { CoaUndoBlockedReasonCategory } from "./coaUndoBlockedReason";

// All undo-related copy lives here so it stays pure, exact, and testable. The
// verbatim strings are required by the spec/tests — do not paraphrase.
export const UNDO_MODAL_TITLE = "Undo change?";

export const NO_SELECTION_UNDO_EXPLANATION =
    "Select at least one entity to undo";

// Deterministic human-readable list: a single name is returned as-is, two names
// are joined with "and", and three or more use an Oxford comma.
export function formatEntityNameList(names: string[]): string {
    if (names.length <= 1) {
        return names[0] ?? "";
    }
    if (names.length === 2) {
        return `${names[0]} and ${names[1]}`;
    }
    const head = names.slice(0, -1).join(", ");
    return `${head}, and ${names[names.length - 1]}`;
}

// Toasts and tooltips can carry many failed entities, so the list is capped and
// the remainder is counted instead of flooding the message.
const ENTITY_NAME_LIST_CAP = 3;

// Names resolve from the loaded workspace list, so `totalCount` can exceed the
// names available. Whatever cannot be named is still counted, so the label never
// reports fewer entities than actually took part.
export function formatCappedEntityLabel({
    names,
    totalCount,
}: {
    names: string[];
    totalCount: number;
}): string {
    if (names.length === 0) {
        return pluralizeWithCount("entity", totalCount);
    }
    const namedList = names.slice(0, ENTITY_NAME_LIST_CAP);
    const remainingCount =
        Math.max(totalCount, names.length) - namedList.length;
    if (remainingCount === 0) {
        return formatEntityNameList(namedList);
    }
    return formatEntityNameList([...namedList, `${remainingCount} more`]);
}

const blockedReasonClauses: Record<
    Exclude<CoaUndoBlockedReasonCategory, "generic">,
    string
> = {
    changed: "changed since applied",
    postApplyActivity: "new activity was posted after the template was applied",
    inUse: "an affected account is in use",
};

export function getBlockedUndoTooltip(
    names: string[],
    category: CoaUndoBlockedReasonCategory,
): string {
    let clause: string;
    if (category === "generic") {
        const entityNoun = pluralize("entity", names.length);
        const verb = names.length === 1 ? "has" : "have";
        clause = `selected ${entityNoun} ${verb} changes or activity that prevent undo`;
    } else {
        clause = blockedReasonClauses[category];
    }
    return `Can’t undo ${formatEntityNameList(names)} — ${clause}`;
}

export function getGroupedUndoModalQuestion({
    selectedEntityCount,
    eligibleActionCount,
}: {
    selectedEntityCount: number;
    eligibleActionCount: number;
}): string {
    const actionNoun = pluralize("change", eligibleActionCount);
    return `This will undo ${eligibleActionCount} eligible ${actionNoun} for ${pluralizeWithCount(
        "entity",
        selectedEntityCount,
    )}.`;
}
